/**
 * Source-document extraction-route evidence for review.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import type {
  LeadFactoryExtractionJob,
  LeadFactorySourceDocumentReviewResponse
} from "./dashboard-lead-factory-types.js";

interface SourceExtractionRouteEvidence {
  route: string;
  status: string;
  terminalState: string;
  extractorVersions: string[];
  factFields: string[];
  sourcePages: string[];
  factCount: number;
  detail: string;
}

export function sourceExtractionRouteEvidence(review: LeadFactorySourceDocumentReviewResponse): SourceExtractionRouteEvidence[] {
  const routes = new Map<
    string,
    {
      statuses: Set<string>;
      terminalStates: Set<string>;
      extractorVersions: Set<string>;
      factFields: Set<string>;
      sourcePages: Set<string>;
      excerpts: string[];
      factCount: number;
    }
  >();
  const ensureRoute = (route: string) => {
    const existing = routes.get(route);
    if (existing) return existing;
    const created = {
      statuses: new Set<string>(),
      terminalStates: new Set<string>(),
      extractorVersions: new Set<string>(),
      factFields: new Set<string>(),
      sourcePages: new Set<string>(),
      excerpts: [] as string[],
      factCount: 0
    };
    routes.set(route, created);
    return created;
  };

  for (const job of review.extractionJobs) {
    const route = extractionRouteFromVersion(job.extractor_version);
    const evidence = ensureRoute(route);
    evidence.statuses.add(job.status);
    evidence.extractorVersions.add(job.extractor_version);
    if (job.failure_class) evidence.terminalStates.add(job.failure_class);
  }

  for (const fact of review.facts) {
    const provenance = parseFactProvenanceForRoute(fact.provenance);
    const route = extractionRouteFromProvenance(provenance, fact.provenance);
    const evidence = ensureRoute(route);
    evidence.factCount += 1;
    evidence.factFields.add(fact.field_name);
    const sourcePage = provenance.sourcePage;
    if (typeof sourcePage === "string" || typeof sourcePage === "number") evidence.sourcePages.add(String(sourcePage));
    const terminalState = extractionTerminalStateFromProvenance(provenance);
    if (terminalState) evidence.terminalStates.add(terminalState);
    const excerpt = provenance.evidenceExcerpt;
    if (typeof excerpt === "string" && excerpt.trim() && evidence.excerpts.length < 2) evidence.excerpts.push(excerpt.trim());
    const routeEvidence = unknownRecord(provenance.routeEvidence);
    const routeVersion = routeEvidence && typeof routeEvidence.hybridEngineVersion === "string" ? routeEvidence.hybridEngineVersion : null;
    if (routeVersion) evidence.extractorVersions.add(routeVersion);
  }

  return Array.from(routes.entries())
    .map(([route, evidence]) => {
      const factFields = Array.from(evidence.factFields).sort();
      const sourcePages = Array.from(evidence.sourcePages).sort((left, right) => Number(left) - Number(right));
      const extractorVersions = Array.from(evidence.extractorVersions).sort();
      const terminalStates = Array.from(evidence.terminalStates).sort();
      const statuses = Array.from(evidence.statuses).sort();
      const fieldSummary = factFields.length > 0 ? `Fields: ${factFields.map(labelize).join(", ")}` : "No fields captured";
      const pageSummary = sourcePages.length > 0 ? `Pages: ${sourcePages.join(", ")}` : "No page evidence";
      const excerptSummary = evidence.excerpts.length > 0 ? evidence.excerpts.join(" | ").slice(0, 220) : "";
      return {
        route,
        status: statuses.join(", ") || (evidence.factCount > 0 ? "facts_recorded" : "unknown"),
        terminalState: terminalStates.join(", ") || "none",
        extractorVersions,
        factFields,
        sourcePages,
        factCount: evidence.factCount,
        detail: [fieldSummary, pageSummary, excerptSummary].filter(Boolean).join(" · ")
      };
    })
    .sort((left, right) => extractionRouteOrder(left.route) - extractionRouteOrder(right.route));
}

export function extractionRouteEvidenceHtml(evidence: SourceExtractionRouteEvidence): string {
  const versions = evidence.extractorVersions.length > 0 ? evidence.extractorVersions.join(", ") : "unknown extractor";
  return `
    <article class="task-item compact" data-source-extraction-route="${escapeAttr(evidence.route)}">
      <div>
        <strong>${escapeHtml(labelize(evidence.route))}</strong>
        <small>${escapeHtml(evidence.status)} · ${escapeHtml(versions)} · ${formatInteger(evidence.factCount)} fact${evidence.factCount === 1 ? "" : "s"}</small>
        <p>${escapeHtml(evidence.detail)}</p>
      </div>
      ${pill(labelize(evidence.terminalState), evidence.terminalState)}
    </article>
  `;
}

export function extractionRouteFromVersion(version: string): string {
  if (/^pdf-text|embedded/i.test(version)) return "embedded_text";
  if (/ocr-text/i.test(version)) return "ocr_text";
  if (/rendered-page|layout/i.test(version)) return "rendered_page_layout";
  if (/multimodal|image/i.test(version)) return "multimodal_image";
  if (/hybrid-pdf-field/i.test(version)) return "hybrid";
  return "unknown";
}

export function extractionRouteFromProvenance(provenance: Record<string, unknown>, fallback: string): string {
  if (typeof provenance.extractionRoute === "string" && provenance.extractionRoute.trim()) return provenance.extractionRoute;
  const source = typeof provenance.source === "string" ? provenance.source : fallback;
  if (source.startsWith("pdf_text.")) return "embedded_text";
  if (source.startsWith("ocr_text.")) return "ocr_text";
  if (source.startsWith("rendered_page_layout.")) return "rendered_page_layout";
  if (source.startsWith("multimodal_image.")) return "multimodal_image";
  return "unknown";
}

export function extractionTerminalStateFromProvenance(provenance: Record<string, unknown>): string | null {
  if (typeof provenance.terminalState === "string") return provenance.terminalState;
  const routeEvidence = unknownRecord(provenance.routeEvidence);
  if (routeEvidence && typeof routeEvidence.terminalState === "string") return routeEvidence.terminalState;
  return null;
}

export function parseFactProvenanceForRoute(provenance: string): Record<string, unknown> {
  if (!provenance.trim().startsWith("{")) return { source: provenance };
  try {
    const parsed = JSON.parse(provenance) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : { source: provenance };
  } catch {
    return { source: provenance };
  }
}

export function unknownRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function extractionRouteOrder(route: string): number {
  if (route === "embedded_text") return 0;
  if (route === "ocr_text") return 1;
  if (route === "rendered_page_layout") return 2;
  if (route === "multimodal_image") return 3;
  if (route === "hybrid") return 4;
  return 5;
}

export function extractionJobHtml(job: LeadFactoryExtractionJob): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(job.extractor_version)}</strong>
        <small>${escapeHtml(job.status)} · ${escapeHtml(job.finished_at || job.created_at)}</small>
        <p>${escapeHtml(job.failure_class || "No failure class.")}</p>
      </div>
    </article>
  `;
}

